import React, { useCallback, useEffect, useState } from 'react';
import { X, Plus, Trash2, CalendarOff, Lightbulb } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/axios';
import Dropdown from './Dropdown';
import CModal from './shared/c-modal';
import useLatestRequest from '../hooks/useLatestRequest';
import { addDays, companyToday } from '../utils/dateUtils';
import { EMPLOYMENT_TYPES } from '../constants';

const WEEKDAYS = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' }, { value: 0, label: 'Sun' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

// The expected working day per employment type. An employee's own daily target
// hours, set on their record, is the exception that overrides these.
const DEFAULT_TARGET_HOURS = {
  'Full-time': 8, 'Part-time': 4, Contract: 8, Intern: 8, Trainee: 8,
};

// One number per employment type, in the order the column header names them, so the cell stays one line
// in a table that already carries eight others.
const targetHoursSummary = (hours) => (hours
  ? EMPLOYMENT_TYPES.map((type) => (hours[type] == null ? '-' : hours[type])).join(' / ')
  : '-');

const currentMonth = () => companyToday().slice(0, 7);

const hhmm = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
const dayNames = (days) => (days.length
  ? WEEKDAYS.filter((d) => days.includes(d.value)).map((d) => d.label).join(', ')
  : 'none');

/** The company's next Monday, the natural date for a policy change to start. */
const nextMonday = () => {
  const today = companyToday();
  const weekday = new Date(Date.parse(today)).getUTCDay();
  return addDays(today, (8 - weekday) % 7 || 7);
};

/**
 * Attendance policy is DATED: each era applies from its date until the next one.
 * Adding an era never rewrites stored attendance - is_late and is_wfo_day are
 * computed at ingest time and read back verbatim, and the nightly job only
 * ingests the current day. A new era simply governs every day ingested from its
 * date onward.
 */
const AttendancePolicyModal = ({ onClose, onSaved }) => {
  const [eras, setEras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    effective_from: nextMonday(), wfo_weekdays: [1], deadline_hour: 12, deadline_minute: 30, note: '',
    start: '09:00', end: '18:00', amendment_window_days: 7, wfh_needs_approval: true,
    daily_target_hours: DEFAULT_TARGET_HOURS,
  });
  const [holidays, setHolidays] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [holidayForm, setHolidayForm] = useState({ holiday_date: '', name: '' });

  // Only the latest load may fill the form: StrictMode runs the mount effect twice and
  // saving a holiday loads again, so a late response would otherwise put the stored
  // daily target back over the one the admin has typed.
  const beginRequest = useLatestRequest();
  const load = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const [eraRes, holRes] = await Promise.all([
        api.get('/api/attendance/policies'),
        api.get('/api/attendance/holidays'),
      ]);
      if (!isCurrent()) return;
      const loadedEras = eraRes.data.data || [];
      setEras(loadedEras);
      setHolidays(holRes.data.data || []);
      // A new era starts from the targets already in force, so an admin changing
      // only the deadline does not silently reset everyone's expected day.
      const newest = loadedEras[0];
      if (newest && newest.daily_target_hours) {
        // Over the defaults, so a type the stored era does not name yet (Trainee, before
        // the migration that adds it) still has a value and the form can save.
        setForm((current) => ({ ...current, daily_target_hours: { ...DEFAULT_TARGET_HOURS, ...newest.daily_target_hours } }));
      }
      // Suggestions are best-effort: a missing month should not break the dialog.
      try {
        const s = await api.get(`/api/attendance/holidays/suggestions?month=${currentMonth()}`);
        if (isCurrent()) setSuggestions(s.data.data || []);
      } catch (e) { if (isCurrent()) setSuggestions([]); }
    } catch (err) {
      if (isCurrent()) setError('Could not load the attendance policy.');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [beginRequest]);

  const saveHoliday = async (holidayDate, name) => {
    if (!holidayDate || !name.trim()) {
      setError('A holiday needs both a date and a name.');
      return;
    }
    try {
      const res = await api.put('/api/attendance/holidays', {
        holiday_date: holidayDate, name: name.trim(),
      });
      toast.success('Holiday saved');
      if (res.data.note) toast(res.data.note, { duration: 8000 });
      setHolidayForm({ holiday_date: '', name: '' });
      await load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message)
        || 'Could not save the holiday.');
    }
  };

  const removeHoliday = async (id) => {
    try {
      await api.delete(`/api/attendance/holidays/${id}`);
      toast.success('Holiday removed');
      await load();
    } catch (err) {
      setError('Could not remove that holiday.');
    }
  };

  useEffect(() => { load(); }, [load]);

  const toggleDay = (value) => setForm((f) => ({
    ...f,
    wfo_weekdays: f.wfo_weekdays.includes(value)
      ? f.wfo_weekdays.filter((d) => d !== value)
      : [...f.wfo_weekdays, value].sort(),
  }));

  const save = async () => {
    const windowDays = Number(form.amendment_window_days);
    if (!form.start || !form.end) {
      setError('Office hours need both a start and an end time.');
      return;
    }
    if (!Number.isInteger(windowDays) || windowDays < 0) {
      setError('The amendment window must be a whole number of days, 0 or more.');
      return;
    }
    const targetHours = {};
    for (const type of EMPLOYMENT_TYPES) {
      const hours = Number(form.daily_target_hours[type]);
      if (form.daily_target_hours[type] === '' || Number.isNaN(hours) || hours < 0 || hours > 24) {
        setError('Daily target hours must be between 0 and 24 for every employment type.');
        return;
      }
      targetHours[type] = hours;
    }
    const [startHour, startMinute] = form.start.split(':').map(Number);
    const [endHour, endMinute] = form.end.split(':').map(Number);
    setSaving(true);
    setError(null);
    try {
      const res = await api.put('/api/attendance/policies', {
        effective_from: form.effective_from,
        wfo_weekdays: form.wfo_weekdays,
        deadline_hour: Number(form.deadline_hour),
        deadline_minute: Number(form.deadline_minute),
        note: form.note.trim() || null,
        start_hour: startHour,
        start_minute: startMinute,
        end_hour: endHour,
        end_minute: endMinute,
        amendment_window_days: windowDays,
        wfh_needs_approval: form.wfh_needs_approval,
        daily_target_hours: targetHours,
      });
      toast.success('Attendance policy updated');
      if (res.data.note) toast(res.data.note, { duration: 8000 });
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message)
        || 'Could not save the policy.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/attendance/policies/${id}`);
      toast.success('Policy era removed');
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message)
        || 'Could not remove that era.');
    }
  };

  return (
    <CModal
      onClose={onClose}
      labelledBy="policy-title"
      dismissible={!saving}
      closeOnBackdrop={false}
      overlayClassName="bg-black/40"
      className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
    >
      <div className="flex shrink-0 items-start justify-between border-b border-gray-200 p-4">
        <div>
          <h2 id="policy-title" className="text-base font-semibold text-gray-900">Attendance policy</h2>
          <p className="mt-1 text-sm text-gray-500">
            Each entry applies from its date until the next one. Adding one does not change
            attendance already recorded.
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        <div>
          <h3 className="text-sm font-medium text-gray-700">In effect</h3>
          {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
          {loading ? (
            <p className="mt-2 text-sm text-gray-500">Loading...</p>
          ) : (
            <div className="mt-2 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500">From</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500">Office days</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500">Sign in by</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500">Hours</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500">
                      Daily target
                      <span className="block font-normal text-gray-400">Full / Part / Con / Int / Tr</span>
                    </th>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500">Amend within</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500">WFH approval</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500">Note</th>
                    <th scope="col" className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {eras.map((era) => (
                    <tr key={era.id} data-testid={`era-${era.effective_from}`}>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-900">
                        {era.effective_from === '0001-01-01' ? 'the beginning' : era.effective_from}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{dayNames(era.wfo_weekdays)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                        {hhmm(era.deadline_hour, era.deadline_minute)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                        {era.start_hour == null || era.end_hour == null
                          ? '-'
                          : `${hhmm(era.start_hour, era.start_minute || 0)}`
                            + ` - ${hhmm(era.end_hour, era.end_minute || 0)}`}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                        {targetHoursSummary(era.daily_target_hours)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                        {era.amendment_window_days == null ? '-' : `${era.amendment_window_days} days`}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {era.wfh_needs_approval == null ? '-' : (era.wfh_needs_approval ? 'Required' : 'Not required')}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{era.note || '-'}</td>
                      <td className="px-3 py-2 text-right">
                        {eras.length > 1 && (
                          <button type="button" onClick={() => remove(era.id)}
                            aria-label={`Remove the era starting ${era.effective_from}`}
                            className="text-gray-400 hover:text-red-600">
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
          <h3 className="text-sm font-medium text-gray-700">Add a change</h3>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="policy-from" className="block text-sm font-medium text-gray-700">
                Starts on
              </label>
              <input id="policy-from" type="date" value={form.effective_from}
                onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm" />
            </div>
            <div>
              <label htmlFor="policy-note" className="block text-sm font-medium text-gray-700">
                Note (optional)
              </label>
              <input id="policy-note" type="text" value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Why is this changing?"
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm" />
            </div>
          </div>

          <fieldset className="mt-3">
            <legend className="text-sm font-medium text-gray-700">Mandatory office days</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {WEEKDAYS.map((d) => {
                const on = form.wfo_weekdays.includes(d.value);
                return (
                  <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                    aria-pressed={on} aria-label={d.label}
                    className={`rounded-md border px-3 py-1.5 text-sm font-medium ${on
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                    {d.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Working from home on one of these is flagged. Select none for no mandatory day.
            </p>
          </fieldset>

          <div className="mt-3 flex items-end gap-3">
            <div className="w-28">
              <label htmlFor="policy-hour" className="block text-sm font-medium text-gray-700">
                Sign in by
              </label>
              <Dropdown id="policy-hour" value={String(form.deadline_hour)}
                onChange={(e) => setForm((f) => ({ ...f, deadline_hour: e.target.value }))}
                className="mt-1">
                {HOURS.map((h) => <option key={h} value={String(h)}>{String(h).padStart(2, '0')}</option>)}
              </Dropdown>
            </div>
            <div className="w-28">
              <label htmlFor="policy-minute" className="block text-sm font-medium text-gray-700">
                Minutes
              </label>
              <Dropdown id="policy-minute" value={String(form.deadline_minute)}
                onChange={(e) => setForm((f) => ({ ...f, deadline_minute: e.target.value }))}
                className="mt-1">
                {MINUTES.map((m) => <option key={m} value={String(m)}>{String(m).padStart(2, '0')}</option>)}
              </Dropdown>
            </div>
            <p className="pb-2 text-xs text-gray-500">
              The whole minute counts as on time. Part-time, contract, intern and trainee staff are always exempt.
            </p>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <label htmlFor="policy-start" className="block text-sm font-medium text-gray-700">
                Office hours start
              </label>
              <input id="policy-start" type="time" value={form.start}
                onChange={(event) => setForm((current) => ({ ...current, start: event.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm" />
            </div>
            <div>
              <label htmlFor="policy-end" className="block text-sm font-medium text-gray-700">
                Office hours end
              </label>
              <input id="policy-end" type="time" value={form.end}
                onChange={(event) => setForm((current) => ({ ...current, end: event.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm" />
            </div>
            <div>
              <label htmlFor="policy-window" className="block text-sm font-medium text-gray-700">
                Amendment window (days)
              </label>
              <input id="policy-window" type="number" min="0" step="1" value={form.amendment_window_days}
                onChange={(event) => setForm((current) => (
                  { ...current, amendment_window_days: event.target.value }
                ))}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm" />
            </div>
          </div>
          <fieldset className="mt-3">
            <legend className="text-sm font-medium text-gray-700">Daily target hours</legend>
            <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {EMPLOYMENT_TYPES.map((type) => (
                <div key={type}>
                  <label htmlFor={`policy-target-${type}`} className="block text-xs text-gray-600">
                    {type}
                  </label>
                  <input id={`policy-target-${type}`} type="number" min="0" max="24" step="0.5"
                    value={form.daily_target_hours[type]}
                    onChange={(event) => setForm((current) => ({
                      ...current,
                      daily_target_hours: { ...current.daily_target_hours, [type]: event.target.value },
                    }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm" />
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              The working day each type is measured against. An employee with their own daily
              target hours set on their record overrides this.
            </p>
          </fieldset>
          <label htmlFor="policy-wfh" className="mt-3 flex items-center gap-2 text-sm text-gray-700">
            <input id="policy-wfh" type="checkbox" checked={form.wfh_needs_approval}
              onChange={(event) => setForm((current) => ({ ...current, wfh_needs_approval: event.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-blue-600" />
            Working from home on an office day needs approval
          </label>
          <p className="mt-1 text-xs text-gray-500">
            The app uses these: hours set the expected day and the early-out check, the window is
            how many days back an employee may correct a punch.
          </p>
        </div>

        <div className="border-t border-gray-200 pt-5">
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <CalendarOff className="h-4 w-4 text-gray-400" aria-hidden="true" />
            Public holidays
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Nobody is expected to sign in on these, so the day reads Holiday instead of Absent.
            Pakistan&apos;s fixed dates are pre-filled; Eid and other lunar holidays move each
            year and need adding by hand.
          </p>

          {suggestions.length > 0 && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="flex items-center gap-2 text-xs font-medium text-amber-900">
                <Lightbulb className="h-3.5 w-3.5" aria-hidden="true" />
                Nobody signed in on these days. A holiday, or a failed run?
              </p>
              <ul className="mt-2 space-y-1">
                {suggestions.map((sg) => (
                  <li key={sg.date} data-testid={`suggestion-${sg.date}`}
                    className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-medium text-gray-900">{sg.date}</span>
                    <span className="text-gray-500">{sg.employees} people</span>
                    <button type="button"
                      onClick={() => setHolidayForm({ holiday_date: sg.date, name: '' })}
                      className="rounded border border-gray-300 bg-white px-2 py-0.5 font-medium text-gray-700 hover:bg-gray-50">
                      Name it a holiday
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="holiday-date" className="block text-sm font-medium text-gray-700">Date</label>
              <input id="holiday-date" type="date" value={holidayForm.holiday_date}
                onChange={(e) => setHolidayForm((f) => ({ ...f, holiday_date: e.target.value }))}
                className="mt-1 block rounded-md border border-gray-300 p-2 text-sm" />
            </div>
            <div className="min-w-[12rem] flex-1">
              <label htmlFor="holiday-name" className="block text-sm font-medium text-gray-700">Name</label>
              <input id="holiday-name" type="text" value={holidayForm.name}
                onChange={(e) => setHolidayForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Eid al-Fitr"
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm" />
            </div>
            <button type="button"
              onClick={() => saveHoliday(holidayForm.holiday_date, holidayForm.name)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add holiday
            </button>
          </div>

          {holidays.length > 0 && (
            <ul className="mt-3 divide-y divide-gray-100 rounded-md border border-gray-200">
              {holidays.map((h) => (
                <li key={h.id} data-testid={`holiday-${h.holiday_date}`}
                  className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>
                    <span className="font-medium text-gray-900">{h.holiday_date}</span>
                    <span className="ml-2 text-gray-600">{h.name}</span>
                  </span>
                  <button type="button" onClick={() => removeHoliday(h.id)}
                    aria-label={`Remove ${h.name} on ${h.holiday_date}`}
                    className="text-gray-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 p-4">
        <button type="button" onClick={onClose}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Close
        </button>
        <button type="button" disabled={saving} onClick={save}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          <Plus className="h-4 w-4" aria-hidden="true" />
          {saving ? 'Saving...' : 'Save change'}
        </button>
      </div>
    </CModal>
  );
};

export default AttendancePolicyModal;
